import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import { Alert, Button } from '@sentinel/ui';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const styles = stylex.create({
  card: {
    display: 'grid',
    gap: '0.875rem',
    paddingTop: '1.25rem',
    paddingBottom: '1.25rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.625rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 60%, transparent)`,
  },
  header: { display: 'grid', gap: '0.25rem' },
  title: { margin: 0, fontSize: '1.0625rem', fontWeight: 600 },
  subtitle: { margin: 0, fontSize: '0.875rem', color: sentinel.textMuted },
  archive: {
    marginTop: '0.25rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderDefault,
    borderRadius: '0.5rem',
    backgroundColor: `color-mix(in oklab, ${sentinel.surfaceElevated} 40%, transparent)`,
    padding: '0.75rem 0.875rem',
  },
  archiveHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.8125rem',
    color: `color-mix(in oklab, ${sentinel.surfaceInverse} 72%, transparent)`,
    marginBottom: '0.5rem',
  },
  fileCount: { color: `color-mix(in oklab, ${sentinel.surfaceInverse} 50%, transparent)` },
  grid: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
    gap: '0.25rem 1rem',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.8125rem',
    color: `color-mix(in oklab, ${sentinel.surfaceInverse} 62%, transparent)`,
  },
  cat: {
    opacity: 0,
    transform: 'translateY(2px)',
    animationName: stylex.keyframes({ to: { opacity: 1, transform: 'translateY(0)' } }),
    animationDuration: '180ms',
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      opacity: 1,
      transform: 'none',
      animationName: 'none',
    },
  },
  manifestRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTopWidth: '1px',
    borderTopStyle: 'dashed',
    borderTopColor: `color-mix(in oklab, ${sentinel.surfaceInverse} 10%, transparent)`,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '0.8125rem',
    color: 'oklch(72% 0.12 264 / 0.85)',
  },
  // 0.55 alpha keeps this the most muted label in the card while clearing
  // WCAG AA 4.5:1 against the composited archive background (axe contrast
  // ~5.5:1; 0.45 previously landed at 4.1:1 and failed the a11y sweep).
  manifestHint: { color: `color-mix(in oklab, ${sentinel.surfaceInverse} 55%, transparent)` },
});

// Mirrors the per-category export sections produced by the backend
// (sentinel-app: ExportDataUseCase::export_sections). Keep in sync; the
// "+ 1" in the file count below accounts for manifest.json.
const CATEGORY_KEYS = [
  'export_cat_profile',
  'export_cat_sessions',
  'export_cat_audit',
  'export_cat_api_keys',
  'export_cat_devices',
  'export_cat_linked',
  'export_cat_consents',
  'export_cat_passkeys',
  'export_cat_login_history',
  'export_cat_org',
  'export_cat_tenant',
  'export_cat_groups',
  'export_cat_mfa',
] as const;

export interface ExportCardProps {
  pending: boolean;
  successFilename: string | null;
  errorMessage: string | null;
  onExport: () => void;
}

/**
 * Presentational card for the export-account-data action. The page owns the
 * mutation; this component only renders status and exposes the trigger.
 */
export function ExportCard({ pending, successFilename, errorMessage, onExport }: ExportCardProps) {
  const { t } = useTranslation('data');
  return (
    <section
      {...stylex.props(styles.card)}
      data-testid="export-card"
      aria-labelledby="export-card-title"
    >
      <header {...stylex.props(styles.header)}>
        <h2 id="export-card-title" {...stylex.props(styles.title)}>
          {t('export_card_title')}
        </h2>
        <p {...stylex.props(styles.subtitle)}>{t('export_card_subtitle')}</p>
      </header>
      <div {...stylex.props(styles.archive)}>
        <div {...stylex.props(styles.archiveHead)}>
          <span>{t('export_archive_label')}</span>
          <span {...stylex.props(styles.fileCount)}>
            {t('export_file_count', { count: CATEGORY_KEYS.length + 1 })}
          </span>
        </div>
        <ul {...stylex.props(styles.grid)} aria-label={t('export_archive_list_label')}>
          {CATEGORY_KEYS.map((key, i) => (
            <li key={key} {...stylex.props(styles.cat)} style={{ animationDelay: `${i * 24}ms` }}>
              {t(key)}
            </li>
          ))}
        </ul>
        <div {...stylex.props(styles.manifestRow)}>
          <span>manifest.json</span>
          <span {...stylex.props(styles.manifestHint)}>{t('export_manifest_hint')}</span>
        </div>
      </div>
      {errorMessage ? (
        <Alert type="error" role="alert">
          {errorMessage}
        </Alert>
      ) : null}
      {successFilename ? (
        <Alert type="info" role="status">
          {t('export_success', { filename: successFilename })}
        </Alert>
      ) : null}
      <div>
        <Button
          type="button"
          variant="primary"
          onClick={onExport}
          disabled={pending}
          data-testid="export-button"
        >
          {pending ? t('export_pending') : t('export_button')}
        </Button>
      </div>
    </section>
  );
}
